import { gql } from '@apollo/client';

export const getBaseUserQuery = gql`
  query getBaseUser {
    isAccessTokenValid {
      status
      consent
      baseUser {
        firstname
        surname
        organization {
          name
        }
        username
      }
    }
  }
`;

export const checkSsoQuery = gql`
  query checkSso($email: String!) {
    checkSso(email: $email)
  }
`;

export const isAccessTokenValidQuery = gql`
  query isAccessTokenValid {
    isAccessTokenValid {
      status
      consent
      user {
        id
        username
        firstname
        surname
        createdAt
        systemRole
        consent
        marketingConsent
        allowedMoreOrganizations
        organization {
          id
        }
      }
    }
  }
`;

export const getDashboardQuery = gql`
  query dashboard($languageCode: String) {
    user(languageCode: $languageCode) {
      id
      organization {
        id
        currentSurveys {
          id
          accessKey
          status
          whitelistedRespondentsLength
          public
          resultsInfo {
            answered
            total
          }
          topLevelReport {
            accessKey
          }
          isReportPendingApproval
          notstartedReason
          user {
            id
          }
          topic {
            id
            name
          }
          topicGroup {
            id
            name
            categoryCode
            typeCode
          }
          sendAt
          endsAt
          reminders {
            reminderAt
          }
        }
        activeProcessSurveys {
          from
          id
          status
          name
          topicGroup {
            id
            name
            categoryCode
            typeCode
          }
          topLevelReport {
            accessKey
          }
          hasOutOfLicenceInterval
          notstartedReason
        }
        activeOnboadings {
          id
          name
          topicGroup {
            id
            name
          }
          topLevelReport {
            accessKey
          }
          steps {
            processStep {
              stepTopicGroup {
                id
                chatSatisfactionScale
                name
              }
            }
            avgMetric
          }
        }
      }
    }
  }
`;

export const getRespondentsQuery = gql`
  query getRespondents($languageCode: String) {
    user(languageCode: $languageCode) {
      respondents {
        id
        organization {
          id
        }
        organizationRole
        contacts {
          source
          type
          value
        }
      }
    }
  }
`;

export const getUserQuery = gql`
  query user($languageCode: String) {
    user(languageCode: $languageCode) {
      id
      username
      systemRole
      organization {
        id
        name
        systemName
        status
        primaryLanguageId
        primaryLanguageCode
        languages {
          id
          code
        }
        respondentsCount
        enabledRespondentsCount
        activeRespondentsInCurrentPeriodCount
        productVersion {
          product
          licenceCount
          validFrom
          validTo
        }
        productVersionHistory {
          product
          licenceCount
          validFrom
          validTo
        }
        beforeFirstSurvey
        config {
          timezone
          testSurveys
        }
        surveys {
          id
          status
          public
          archivedAt
          resultsInfo {
            answered
            total
          }
          topLevelReport {
            id
            accessKey
          }
          isReportPendingApproval
          notstartedReason
          topic {
            id
            name
            description
            topicGroup {
              id
            }
          }
          topicGroup {
            id
            name
            description
            categoryCode
          }
          sendAt
          updatedAt
          endsAt
          reminders {
            reminderAt
          }
          availableInLanguages {
            id
            code
          }
        }
        surveyGroups {
          from
          to
          id
          status
          name
          topicGroup {
            id
            name
            typeCode
            categoryCode
            translations {
              id
              language {
                code
              }
              value
              description
            }
            languages {
              id
              code
            }
            description
            typeCode
            parentTopicGroup {
              id
            }
          }
          reports {
            id
            respondent {
              id
            }
            accessKey
          }
          statistics {
            total
            answered
          }
          steps {
            hasOutOfLicenceInterval
            hasNotStarted
            status
            notstartedReason
            processStep {
              stepTopicGroup {
                id
                name
                languages {
                  id
                  code
                }
                description
                categoryCode
              }
            }
            surveys {
              sendAt
              id
              archivedAt
            }
            avgMetric
          }
        }
        featureNames
      }
    }
  }
`;

export const getOrganizationSurveysQuery = gql`
  query organizationSurveys($organizationId: ID!, $languageCode: String) {
    organizationSurveys(organizationId: $organizationId, languageCode: $languageCode) {
      id
      status
      whitelistedRespondents {
        id
        firstname
        surname
      }
      public
      archivedAt
      resultsInfo {
        answered
        total
      }
      topLevelReport {
        id
        accessKey
      }
      isReportPendingApproval
      notstartedReason
      user {
        id
      }
      topicGroup {
        id
        name
        description
        categoryCode
        typeCode
      }
      sendAt
      updatedAt
      endsAt
      reminders {
        reminderAt
      }
      availableInLanguages {
        id
        code
      }
    }
  }
`;

export const getAllowedTopicGroups = gql`
  query allowedTopicGroups($languageCode: String) {
    allowedTopicGroups(languageCode: $languageCode) {
      id
      name
      description
      containsSteps
      typeCode
      lastSurvey {
        id
        sendAt
        endsAt
        status
      }
      steps {
        startOffsetMins
        reminders {
          reminderOffsetMins
        }
        endOffsetMins
        triggeredOn
        stepTopicGroup {
          id
          name
          translations {
            language {
              id
              code
            }
            value
          }
        }
      }
      lastSurveyGroup {
        id
        from
        status
      }
      languages {
        id
        code
      }
      organization {
        id
      }
      lastValidTopic {
        id
        numberOfComments
        name
        isValid
        languageIdsOnStartingQuestion
        updatedAt
        updatedBy {
          firstname
          surname
        }
        createdAt
        topicGroup {
          translations {
            language {
              id
              code
            }
            value
          }
          id
        }
        languages {
          id
          code
        }
      }
      categoryCode
      categoryName
      parentTopicGroup {
        id
      }
    }
  }
`;

export const getOrganizationRespondentsQuery = gql`
  query activeRespondents($languageCode: String) {
    user(languageCode: $languageCode) {
      id
      organization {
        id
        enabledRespondentsCount
        name
        activeRespondents {
          id
          preferredContactType
          firstname
          surname
          language {
            id
            code
          }
          team {
            id
            name
          }
          contacts {
            type
            source
            value
            phoneNumber {
              countryCallingCode
              nationalNumber
            }
          }
        }
        teams {
          id
          name
          rootTeam
          structureId
          leader {
            id
            firstname
            surname
            contacts {
              type
              source
              value
            }
          }
          supervisorTeam {
            id
          }
          members {
            id
            firstname
            surname
            preferredContactType
            contacts {
              type
              source
              value
              phoneNumber {
                countryCallingCode
                nationalNumber
              }
            }
            language {
              id
              code
            }
            disabled
          }
        }
      }
    }
  }
`;

export const getTopicGroupDetail = gql`
  query topicGroupDetail($topicGroupId: ID!, $languageCode: String) {
    topicGroup(topicGroupId: $topicGroupId, languageCode: $languageCode) {
      id
      name
      containsSteps
      accessKeys {
        id
        level
        accessKey
      }
      translations {
        language {
          id
          code
        }
        value
        id
        description
      }
      languages {
        id
        code
      }
      description
      questionOrderManuallyModified
      questionOrderModified
      lastValidTopic {
        id
        name
        description
        isApproved
        isEditable
        updatedAt
        updatedBy {
          firstname
          surname
        }
        languages {
          id
          code
        }
        startingQuestion {
          id
        }
        allQuestions {
          id
          text
          selectedRange
          index
          reportIndex
          reportOrder
          optional
          randomizeOrder
          isRankingScale
          showEscapeOption
          answersToHighlight
          isFirstInSectionWithNumber
          translations {
            choices
            statements
            isDefault
            text
            language {
              id
              code
            }
          }
          rules {
            id
            nextQuestion {
              id
              index
            }
            origValue
            operator
            order
            isFromDefault
          }
          positionX
          positionY
          metrics
          isQuestionTrigger
        }
        commentGroups {
          id
          positionX
          positionY
          topicId
          comments {
            id
            text
            originator {
              id
              firstname
              surname
            }
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

export const getTopicDetail = gql`
  query topicDetail($topicId: ID, $topicGroupId: ID) {
    topicDetail(topicId: $topicId, topicGroupId: $topicGroupId) {
      id
      name
      description
      isApproved
      isValid
      isEditable
      updatedAt
      updatedBy {
        firstname
        surname
      }
      languages {
        id
        code
      }
      startingQuestion {
        id
      }
      allQuestions {
        id
        text
        selectedRange
        index
        reportIndex
        reportOrder
        optional
        randomizeOrder
        isRankingScale
        showEscapeOption
        answersToHighlight
        isFirstInSectionWithNumber
        translations {
          choices
          statements
          isDefault
          text
          translationStatus
          language {
            id
            code
          }
        }
        rules {
          id
          nextQuestion {
            id
            index
          }
          origValue
          operator
          order
          isFromDefault
        }
        positionX
        positionY
        metrics
        isQuestionTrigger
      }
      commentGroups {
        id
        positionX
        positionY
        topicId
        comments {
          id
          text
          originator {
            id
            firstname
            surname
          }
          createdAt
          updatedAt
        }
      }
      topicGroup {
        id
        name
        organization {
          id
        }
        accessKeys {
          id
          level
          accessKey
        }
        containsSteps
        translations {
          language {
            id
            code
          }
          value
          id
          description
        }
        languages {
          id
          code
        }
        description
        questionOrderManuallyModified
        questionOrderModified
      }
    }
  }
`;

export const getSurvey = gql`
  query survey($surveyId: ID!) {
    survey(surveyId: $surveyId) {
      id
      status
      public
      accessKey
      topicGroup {
        id
        name
        lastValidTopic {
          id
        }
        translations {
          language {
            code
          }
          id
          value
          description
        }
        languages {
          id
          code
        }
        description
      }
      topic {
        id
      }
      channels {
        respondent {
          id
          firstname
          surname
        }
        lastRemindedAt
        isFinished
        answered
        alert
      }
      whitelistedRespondents {
        id
        preferredContactType
        firstname
        surname
        team {
          id
          name
        }
        contacts {
          type
          source
          value
        }
        language {
          id
          code
        }
      }
      resultsInfo {
        total
        answered
        finished
      }
      reports {
        respondent {
          id
        }
        accessKey
      }
      isReportPendingApproval
      teamsResultInfo {
        team {
          id
          name
          leader {
            firstname
            surname
          }
        }
        resultInfo {
          total
          answered
          anonymous
        }
        teamMemberIdsFromChannels
      }
      sendAt
      endsAt
      reminders {
        reminderAt
      }
      sendEmailSms
      anonymitySettings {
        default
        allowedLevels
      }
    }
  }
`;

export const surveyAttachmentsQuery = gql`
  query surveyAttachments($surveyId: ID!) {
    survey(surveyId: $surveyId) {
      id
      attachments {
        id
        originalName
        mimeType
        sizeBytes
        createdAt
      }
    }
  }
`;

export const surveyAttachmentDownloadUrlQuery = gql`
  query surveyAttachmentDownloadUrl($attachmentId: ID!) {
    surveyAttachmentDownloadUrl(attachmentId: $attachmentId)
  }
`;

export const getSurveyGroupWithRespondents = gql`
  query surveyGroupWithRespondents($surveyGroupId: ID!) {
    surveyGroup(surveyGroupId: $surveyGroupId) {
      id
      surveyGroupRespondents {
        respondentId
        addedOn
        deleted
        isCompleted
        numberOfSurveys
      }
    }
  }
`;

export const getSurveyGroupWithoutRespondents = gql`
  query surveyGroupWithoutRespondents($surveyGroupId: ID!) {
    surveyGroup(surveyGroupId: $surveyGroupId) {
      id
      name
      topicGroup {
        id
        name
        translations {
          language {
            code
          }
          id
          value
          description
        }
        categoryCode
        typeCode
        languages {
          id
          code
        }
        steps {
          id
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          triggeredOn
          endOffsetMins
          stepTopicGroup {
            id
            translations {
              value
              description
              language {
                code
              }
            }
          }
        }
        description
      }
      status
      steps {
        processStep {
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          endOffsetMins
          triggeredOn
          stepTopicGroup {
            id
            name
            allowedAnonymities
            defaultAnonymity
            translations {
              id
              language {
                code
              }
              value
            }
            lastValidTopic {
              id
            }
          }
        }
        hasMetricQuestion
        surveys {
          id
          status
        }
      }
      topLevelReport {
        id
        accessKey
      }
    }
  }
`;

export const getSurveyGroupStepStatistics = gql`
  query surveyGroupStepStatistics($surveyGroupId: ID!) {
    surveyGroup(surveyGroupId: $surveyGroupId) {
      id
      steps {
        processStep {
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          endOffsetMins
          stepTopicGroup {
            id
            name
            translations {
              id
              language {
                code
              }
              value
            }
            lastValidTopic {
              id
            }
          }
        }
        daysFromStart
        status
        hasNotStarted
        notstartedReason
        statistics {
          answered
          total
          withProblems
        }
        avgMetric
      }
    }
  }
`;

export const surveyGroupRespondents = gql`
  query surveyGroupRespondents(
    $surveyGroupId: ID!
    $pagination: Pagination
    $searchQuery: String
    $withoutDeleted: Boolean
  ) {
    surveyGroupRespondents(
      surveyGroupId: $surveyGroupId
      pagination: $pagination
      searchQuery: $searchQuery
      withoutDeleted: $withoutDeleted
    ) {
      data {
        respondent {
          id
          preferredContactType
          firstname
          surname
          deleted
          team {
            id
            name
          }
          contacts {
            type
            source
            value
            phoneNumber {
              countryCallingCode
              nationalNumber
            }
          }
        }
        addedOn
        isLastRun
        isCreatingSurveys
        recruiterName
        recruitmentId
        creditsToReturn
        surveys {
          id
          status
          notstartedReason
          sendAt
          endsAt
          topicGroup {
            id
            name
            translations {
              id
              language {
                code
              }
              value
            }
          }
        }
        channels {
          survey {
            id
          }
          finishedAt
          lastRemindedAt
          isFinishedByRespondent
          allowedAnonymities
          respondent {
            id
          }
          answered
          slug
        }
        hiringSurveys {
          id
          status
          notstartedReason
          sendAt
          endsAt
          topicGroup {
            id
            name
            translations {
              id
              language {
                code
              }
              value
            }
          }
          whitelistedRespondents {
            id
          }
        }
        deleted
        deletedAt
        undeliveredInvitations {
          respondentId
          undeliveredSurveyInvitations {
            surveyId
            contact
            status
          }
        }
        answers {
          topicGroupId
          value
        }
      }
      totalCount
    }
  }
`;

export const getDatesForNewSurvey = gql`
  query datesForNewSurvey($organizationId: ID!) {
    datesForNewSurvey(organizationId: $organizationId) {
      sendAt
      endsAt
      reminderAt
      secondReminderAt
    }
  }
`;

export const getOrganizationStructure = gql`
  query Organization($organizationId: ID) {
    organization(organizationId: $organizationId) {
      id
      name
      systemName
      companyId
      primaryLanguageId
      primaryLanguageCode
      languages {
        id
        code
      }
      enabledRespondentsCount
      respondentsCount
      activeRespondentsInCurrentPeriodCount
      productVersion {
        id
        product
        validTo
        licenceCount
        validFrom
        activePeriodEnd
        usedCredits
        boughtCredits
        transferredCredits
      }
      config {
        countryCode
      }
      productVersionHistory {
        id
        product
        licenceCount
        validFrom
        validTo
        boughtCredits
        transferredCredits
        activePeriodEnd
      }
      featureNames
      hasProcessSurveys
      lastSuccessfulApiUpdate
      lastFailedApiUpdate
    }
  }
`;

export const getSurveyGroups = gql`
  query surveyGroups($organizationId: ID) {
    organization(organizationId: $organizationId) {
      surveyGroups {
        id
        name
        status
        from
        topicGroup {
          typeCode
          name
          organization {
            id
          }
          translations {
            value
            language {
              code
            }
          }
          steps {
            id
            startOffsetMins
            reminders {
              reminderOffsetMins
            }
            endOffsetMins
            triggeredOn
            stepTopicGroup {
              id
              translations {
                value
                description
                language {
                  code
                }
              }
            }
          }
        }
        statistics {
          total
          answered
        }
      }
    }
  }
`;

export const getOrganizationTeams = gql`
  query organizationTeams($organizationId: ID!) {
    organizationTeams(organizationId: $organizationId) {
      id
      name
      rootTeam
      structureId
      supervisorTeam {
        id
      }
      leader {
        id
        firstname
        surname
        originator
        contacts {
          type
          source
          value
        }
      }
      supervisorTeam {
        id
      }
      members {
        id
        firstname
        surname
        preferredContactType
        originator
        createdAt
        notificationTeamIds
        contacts {
          type
          source
          value
          phoneNumber {
            countryCallingCode
            nationalNumber
          }
        }
        language {
          id
          code
        }
        disabled
      }
    }
  }
`;

export const getOrganizationAdmins = gql`
  query organizationAdmins($organizationId: ID!) {
    organizationAdmins(organizationId: $organizationId) {
      id
      user {
        id
      }
      contacts {
        source
        type
        value
      }
    }
  }
`;

export const getOrganizationSettings = gql`
  query OrganizationSetting($organizationId: ID!) {
    organization(organizationId: $organizationId) {
      id
      name
      systemName
      primaryLanguageCode
      processNotificationsDefault
      processNotificationsLocked
      activeRespondentsInCurrentPeriodCount
      productVersion {
        product
        id
      }
      productVersionHistory {
        id
        product
        licenceCount
        transferredCredits
        boughtCredits
        activePeriodEnd
        validFrom
        validTo
        usedCredits
      }
      organizationAdmins {
        id
        firstname
        surname
        user {
          id
          username
        }
      }
      languages {
        id
        code
      }
      allRespondents {
        id
        firstname
        surname
        organizationRole
        contacts {
          type
          value
          source
        }
      }
      config {
        timezone
        countryCode
        countryCallingCode
      }
      featureNames
      logo {
        id
        url
        originalName
        createdAt
      }
    }
  }
`;

export const getOrganizationSettingsReport = gql`
  query OrganizationSettingReport($organizationId: ID!) {
    organization(organizationId: $organizationId) {
      id
      name
      primaryLanguageId
      primaryLanguageCode
      teams {
        id
        name
        members {
          id
          firstname
          surname
          contacts {
            type
            value
          }
        }
      }
      languages {
        id
        code
      }
      allRespondents {
        id
        firstname
        surname
        organizationRole
        team {
          id
        }
        disabled
        contacts {
          type
          value
        }
      }
    }
  }
`;

export const getLanguages = gql`
  query Languages($filter: LanguageFilterInput) {
    languages(filter: $filter) {
      id
      code
    }
  }
`;

export const getReportRecipients = gql`
  query ReportRecipients($organizationId: ID!) {
    reportRecipients(organizationId: $organizationId) {
      id
      respondentDeleted
      teamDeleted
      respondent {
        id
        firstname
        surname
        contacts {
          type
          value
        }
      }
      team {
        id
        name
      }
      language {
        id
        code
      }
      type
    }
  }
`;

export const getOrganizationRespondentsCount = gql`
  query getOrganizationRespondentsCount($organizationId: ID!) {
    organization(organizationId: $organizationId) {
      respondentsCount
    }
  }
`;

export const getOrganizationsQuery = gql`
  query getOrganizations {
    user {
      id
      allowedOrganizations {
        id
        name
        systemName
      }
      organization {
        id
        name
        systemName
      }
    }
  }
`;

export const getReportAccesses = gql`
  query ReportAccess($surveyId: ID!) {
    survey(surveyId: $surveyId) {
      sendAt
      endsAt
      isReportPendingApproval
      topic {
        name
        topicGroup {
          name
          translations {
            id
            language {
              code
            }
            value
          }
        }
      }
      status
      reports {
        id
        accessKey
        respondent {
          id
          firstname
          surname
          contacts {
            type
            value
          }
        }
        accepted
        sent
        teams {
          id
          name
          leader {
            id
          }
        }
        readAt
        resultsInfo {
          answered
          total
        }
      }
      resultsInfo {
        answered
        total
      }
      reportRecipients {
        team {
          id
        }
        validFrom
        validTo
        respondent {
          id
        }
      }
    }
  }
`;

export const getSequenceReportAccesses = gql`
  query SequenceReportAccesses($surveyGroupId: ID!) {
    surveyGroup(surveyGroupId: $surveyGroupId) {
      from
      topicGroup {
        id
        name
        translations {
          id
          language {
            code
          }
          value
        }
      }
      status
      isAnySurveyComplete
      steps {
        processStep {
          startOffsetMins
          reminders {
            reminderOffsetMins
          }
          endOffsetMins
          stepTopicGroup {
            id
            name
            translations {
              id
              language {
                code
              }
              value
            }
          }
        }
        daysFromStart
        status
        hasNotStarted
        notstartedReason
        statistics {
          answered
          total
        }
      }
      reports {
        id
        accessKey
        respondent {
          id
          firstname
          surname
          contacts {
            type
            value
          }
        }
        accepted
        sent
        teams {
          id
          name
          leader {
            id
          }
        }
        readAt
        visitsInLastYear
        totalCount
        unreadCount
      }
      reportRecipients {
        team {
          id
        }
        validFrom
        validTo
        respondent {
          id
        }
      }
    }
  }
`;

export const getTopicGroup = gql`
  query topicGroup($topicGroupId: ID!, $language: ID) {
    topicGroup(topicGroupId: $topicGroupId, language: $language) {
      id
      name
      description
      organization {
        id
      }
      translations {
        value
        description
        language {
          id
        }
      }
      steps {
        id
        startOffsetMins
        reminders {
          reminderOffsetMins
        }
        endOffsetMins
        stepTopicGroup {
          id
          organization {
            id
          }
          lastValidTopic {
            id
            name
          }
          lastValidTopic {
            id
          }
          translations {
            value
            description
            language {
              id
              code
            }
          }
        }
      }
      name
      description
      translations {
        language {
          id
          code
        }
        value
        id
        description
      }
      typeCode
    }
  }
`;

export const getTranslationExport = gql`
  query exportTranslations($topicId: ID!, $languageCode: String, $topicGroupId: ID) {
    exportTranslations(topicId: $topicId, languageCode: $languageCode, topicGroupId: $topicGroupId) {
      mimetype
      name
      contents
    }
  }
`;

export const getSurveys = gql`
  query surveys($languageCode: String) {
    user(languageCode: $languageCode) {
      organization {
        surveys {
          id
          public
          status
          whitelistedRespondents {
            id
          }
          resultsInfo {
            answered
            total
          }
          topic {
            name
          }
          topicGroup {
            name
            typeCode
          }
          sendAt
        }
      }
    }
  }
`;

export const getPublicTopicGroups = gql`
  query publicTopicGroups($language: String!, $product: String) {
    publicTopicGroups(language: $language, product: $product) {
      id
      name
    }
  }
`;

export const getLastMessagesForRespondent = gql`
  query lastMessagesForRespondent($surveyIds: [ID!]!, $respondentIds: [ID!]!) {
    lastMessagesForRespondent(surveyIds: $surveyIds, respondentIds: $respondentIds) {
      id
      messageStatus
      processed
      createdAt
      error
      surveyId
    }
  }
`;

export const getCreditHistory = gql`
  query getCreditHistory($productId: ID!) {
    getCreditHistory(productId: $productId) {
      id
      organization {
        id
        name
      }
      type
      creditsUsed
      createdAt
      user {
        id
        firstname
        surname
      }
    }
  }
`;

export const getCompletedSurveys = gql`
  query getCompletedSurveys($organizationId: ID!) {
    getCompletedSurveys(organizationId: $organizationId) {
      id
      topic {
        name
      }
      topicGroup {
        name
      }
      sendAt
      endsAt
      teamIds
      reports {
        id
        accessKey
        respondent {
          id
        }
        teams {
          id
        }
      }
    }
  }
`;
